import { useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { Outlet } from "react-router";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import { GoBackButton, LoadingFrame } from "@covertree/ui";
import { LoadingFrameState } from "@covertree/new-business-redux";

import { getSections } from "./utils/getSections";
import { DeviceLayout } from "./components/DeviceLayout";
import { Theme, useMediaQuery } from "@mui/material";
import { Step } from "./types";

interface StepsLayoutProps<StepPathType> {
  steps: Step<StepPathType>[];
  contactNumber: string;
  contentWidth: string;
  pageTitle: string;
  errorIcon: string;
  showBackButton: boolean;
  currentStep: Step<StepPathType>;
  showErrorIcon: boolean;
  showDisclaimerMessage?: boolean;
  showPageTitle?: boolean;
  loadingFrameState?: LoadingFrameState;
}

export function StepsLayout<StepPathType>({
  steps,
  currentStep,
  pageTitle,
  contactNumber,
  contentWidth,
  errorIcon,
  loadingFrameState = {} as LoadingFrameState,
  showErrorIcon,
  showBackButton,
  showDisclaimerMessage = false,
  showPageTitle = true,
}: StepsLayoutProps<StepPathType>) {
  const layoutRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));
  const sections = useMemo(() => getSections(steps), [steps]);

  useEffect(() => {
    if (isDesktop) {
      layoutRef.current?.scrollTo({
        top: 0,
      });
    } else {
      window.scrollTo(0, 0);
    }
  }, [currentStep]);

  const { show: showLoadingFrame, ...rest } = loadingFrameState;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <DeviceLayout
        ref={layoutRef}
        isDesktop={isDesktop}
        currentStep={currentStep}
        sections={sections}
        contactNumber={contactNumber}
        contentWidth={contentWidth}
        showDisclaimerMessage={showDisclaimerMessage}
        showBackButton={showBackButton}>
        {isDesktop && showBackButton && (
          <GoBackButton buttonStyles={{ marginBottom: 4 }} />
        )}
        {showErrorIcon && (
          <Container
            disableGutters
            sx={{
              margin: "16px 0",
              padding: "0",
              height: "100px",
              width: "100%",
            }}>
            <img src={errorIcon} alt="error" width={100} height={100} />
          </Container>
        )}
        {showPageTitle && (
          <Typography
            variant={isDesktop ? "h3" : "h4"}
            sx={{
              lineHeight: 1.5,
              width: { md: "auto", xs: "100%" },
            }}>
            {pageTitle}
          </Typography>
        )}
        <Outlet />
        {showLoadingFrame && (
          <LoadingFrame
            isFixed={true}
            image="/animations/spinner.gif"
            {...rest}
          />
        )}
      </DeviceLayout>
    </>
  );
}
